import React, { useState, useEffect, useCallback } from 'react';
import { formatDate, formatDateTime } from '@/utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Truck, Package, Clock, CheckCircle, MapPin, Calendar, Plus, 
  Bell, User, LogOut, FileText, History, QrCode, Loader2,
  AlertCircle, Phone, Mail
} from 'lucide-react';

import { PixPagamentoDialog, PagamentoBadge } from '@/components/PixPagamento';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badges
const statusConfig = {
  pendente: { label: 'Pendente', class: 'bg-amber-100 text-amber-800', icon: Clock },
  confirmada: { label: 'Confirmada', class: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  em_transito: { label: 'Em Trânsito', class: 'bg-purple-100 text-purple-800', icon: Truck },
  concluida: { label: 'Concluída', class: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelada: { label: 'Cancelada', class: 'bg-red-100 text-red-800', icon: AlertCircle },
};

const materialStatusConfig = {
  recebido: { label: 'Recebido', class: 'bg-slate-100 text-slate-800' },
  em_preparacao: { label: 'Em Preparação', class: 'bg-amber-100 text-amber-800' },
  em_esterilizacao: { label: 'Em Esterilização', class: 'bg-blue-100 text-blue-800' },
  pronto: { label: 'Pronto', class: 'bg-green-100 text-green-800' },
  entregue: { label: 'Entregue', class: 'bg-slate-100 text-slate-600' },
};

export default function PortalClientePage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('inicio');
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginSenha, setLoginSenha] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Data
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [notificacoes, setNotificacoes] = useState([]);
  
  // Dialogs
  const [showNovaSolicitacao, setShowNovaSolicitacao] = useState(false);
  const [tipoSolicitacao, setTipoSolicitacao] = useState('coleta');
  const [pixSolicitacao, setPixSolicitacao] = useState(null);
  const [frete, setFrete] = useState(null);
  const [contato, setContato] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/portal-cliente/contato`).then(r => r.ok ? r.json() : null).then(setContato).catch(() => {});
  }, []);
  
  // Form nova solicitação
  const [solicitacaoForm, setSolicitacaoForm] = useState({
    tipo: 'coleta',
    data_preferencial: '',
    horario_preferencial: '',
    prioridade: 'normal',
    observacoes: '',
    itens: [{ descricao: '', quantidade: 1 }],
  });

  // Check if logged in
  useEffect(() => {
    const token = localStorage.getItem('cliente_token');
    const userData = localStorage.getItem('cliente_user');
    if (token && userData) {
      setIsLoggedIn(true);
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  // Fetch data when logged in + auto-refresh every 30s
  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cliente_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      fetch(`${API_URL}/api/portal-cliente/frete`, { headers })
        .then(r => r.ok ? r.json() : null).then(setFrete).catch(() => setFrete(null));
      
      const [solRes, matRes, notRes] = await Promise.all([
        fetch(`${API_URL}/api/portal-cliente/solicitacoes`, { headers }),
        fetch(`${API_URL}/api/portal-cliente/meus-materiais`, { headers }),
        fetch(`${API_URL}/api/portal-cliente/notificacoes?apenas_nao_lidas=true`, { headers }),
      ]);
      
      if (solRes.ok) setSolicitacoes(await solRes.json());
      if (matRes.ok) setMateriais(await matRes.json());
      if (notRes.ok) setNotificacoes(await notRes.json());
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/portal-cliente/login?email=${encodeURIComponent(loginEmail)}&senha=${encodeURIComponent(loginSenha)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('cliente_token', data.access_token);
        localStorage.setItem('cliente_user', JSON.stringify(data.usuario));
        setUser(data.usuario);
        setIsLoggedIn(true);
        toast.success('Bem-vindo!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Email ou senha inválidos');
      }
    } catch (error) {
      toast.error('Erro ao fazer login');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('cliente_token');
    localStorage.removeItem('cliente_user');
    setIsLoggedIn(false);
    setUser(null);
    toast.info('Você saiu do portal');
  };

  const handleCriarSolicitacao = async () => {
    if (!solicitacaoForm.data_preferencial) {
      toast.error('Selecione uma data preferencial');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/portal-cliente/solicitacoes`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...solicitacaoForm,
          itens: solicitacaoForm.itens.filter(i => i.descricao.trim()),
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Solicitação ${data.numero} criada com sucesso!`);
        setShowNovaSolicitacao(false);
        if (data.pagamento) setPixSolicitacao(data);
        setSolicitacaoForm({
          tipo: 'coleta',
          data_preferencial: '',
          horario_preferencial: '',
          prioridade: 'normal',
          observacoes: '',
          itens: [{ descricao: '', quantidade: 1 }],
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao criar solicitação');
      }
    } catch (error) {
      toast.error('Erro ao criar solicitação');
    }
  };

  const handleInformarPagamento = async (id) => {
    const res = await fetch(`${API_URL}/api/portal-cliente/solicitacoes/${id}/informar-pagamento`, { method: 'POST', headers: getAuthHeaders() });
    const data = await res.json();
    if (res.ok) { toast.success(data.message); fetchData(); }
    else toast.error(data.detail || 'Erro ao informar pagamento');
  };

  const addItem = () => {
    setSolicitacaoForm(prev => ({
      ...prev,
      itens: [...prev.itens, { descricao: '', quantidade: 1 }]
    }));
  };

  const updateItem = (index, field, value) => {
    setSolicitacaoForm(prev => ({
      ...prev,
      itens: prev.itens.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItem = (index) => {
    if (solicitacaoForm.itens.length > 1) {
      setSolicitacaoForm(prev => ({
        ...prev,
        itens: prev.itens.filter((_, i) => i !== index)
      }));
    }
  };

  // ========== LOGIN PAGE ==========
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
              <Package className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Portal do Cliente</CardTitle>
            <CardDescription>Sterisul - Central de Esterilização</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="senha">Senha</Label>
                <Input
                  id="senha"
                  type="password"
                  value={loginSenha}
                  onChange={(e) => setLoginSenha(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Entrar
              </Button>
            </form>
            
            <div className="mt-6 pt-6 border-t text-center text-sm text-slate-500">
              <p>Não tem acesso? Entre em contato com a {contato?.nome_empresa || 'Sterisul'}.</p>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-2">
                {contato?.telefone && (
                  <a href={`https://wa.me/55${contato.telefone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-emerald-700 hover:underline" data-testid="portal-contato-telefone">
                    <Phone className="w-4 h-4" /> {contato.telefone}
                  </a>
                )}
                {contato?.email && (
                  <a href={`mailto:${contato.email}`} className="flex items-center gap-1 text-blue-700 hover:underline" data-testid="portal-contato-email">
                    <Mail className="w-4 h-4" /> {contato.email}
                  </a>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ========== PORTAL PRINCIPAL ==========
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Portal do Cliente</h1>
              <p className="text-xs text-slate-500">{user?.cliente_nome}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="relative">
              <Bell className="w-5 h-5" />
              {notificacoes.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {notificacoes.length}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="sm">
              <User className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-blue-200 bg-blue-50"
            onClick={() => { setTipoSolicitacao('coleta'); setSolicitacaoForm(f => ({...f, tipo: 'coleta'})); setShowNovaSolicitacao(true); }}
          >
            <CardContent className="pt-4 text-center">
              <Truck className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="font-medium text-blue-900">Solicitar Coleta</p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-green-200 bg-green-50"
            onClick={() => { setTipoSolicitacao('entrega'); setSolicitacaoForm(f => ({...f, tipo: 'entrega'})); setShowNovaSolicitacao(true); }}
          >
            <CardContent className="pt-4 text-center">
              <Package className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="font-medium text-green-900">Solicitar Entrega</p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab('materiais')}
          >
            <CardContent className="pt-4 text-center">
              <FileText className="w-8 h-8 mx-auto text-slate-600 mb-2" />
              <p className="font-medium">Meus Materiais</p>
              <p className="text-xs text-slate-500">{materiais.length} itens</p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setActiveTab('solicitacoes')}
          >
            <CardContent className="pt-4 text-center">
              <History className="w-8 h-8 mx-auto text-slate-600 mb-2" />
              <p className="font-medium">Solicitações</p>
              <p className="text-xs text-slate-500">{solicitacoes.filter(s => s.status === 'pendente').length} pendentes</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="inicio">Início</TabsTrigger>
            <TabsTrigger value="materiais">Meus Materiais</TabsTrigger>
            <TabsTrigger value="solicitacoes">Solicitações</TabsTrigger>
          </TabsList>

          {/* TAB INÍCIO */}
          <TabsContent value="inicio" className="space-y-4 mt-4">
            {/* Solicitações Pendentes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  Solicitações Pendentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {solicitacoes.filter(s => ['pendente', 'confirmada', 'em_transito'].includes(s.status)).length === 0 ? (
                  <p className="text-center text-slate-500 py-4">Nenhuma solicitação em andamento</p>
                ) : (
                  <div className="space-y-3">
                    {solicitacoes.filter(s => ['pendente', 'confirmada', 'em_transito'].includes(s.status)).map(sol => {
                      const status = statusConfig[sol.status] || statusConfig.pendente;
                      const StatusIcon = status.icon;
                      return (
                        <div key={sol.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${sol.tipo === 'coleta' ? 'bg-blue-100' : 'bg-green-100'}`}>
                              {sol.tipo === 'coleta' ? <Truck className="w-4 h-4 text-blue-600" /> : <Package className="w-4 h-4 text-green-600" />}
                            </div>
                            <div>
                              <p className="font-medium">{sol.numero}</p>
                              <p className="text-sm text-slate-500">
                                {sol.tipo.charAt(0).toUpperCase() + sol.tipo.slice(1)} - {sol.data_preferencial}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap justify-end">
                            {sol.pagamento && sol.pagamento.status !== 'pago' && (
                              <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => setPixSolicitacao(sol)} data-testid={`btn-pagar-pix-inicio-${sol.id}`}>
                                <QrCode className="w-3.5 h-3.5 mr-1" /> Pagar frete
                              </Button>
                            )}
                            <Badge className={status.class}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Materiais Prontos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Materiais Prontos para Retirada
                </CardTitle>
              </CardHeader>
              <CardContent>
                {materiais.filter(m => m.status === 'pronto').length === 0 ? (
                  <p className="text-center text-slate-500 py-4">Nenhum material pronto no momento</p>
                ) : (
                  <div className="space-y-2">
                    {materiais.filter(m => m.status === 'pronto').slice(0, 5).map(mat => (
                      <div key={mat.material_id} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                        <span className="font-medium">{mat.material_nome}</span>
                        <Badge className="bg-green-100 text-green-800">Pronto</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB MATERIAIS */}
          <TabsContent value="materiais" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Acompanhamento de Materiais</CardTitle>
                <CardDescription>Veja o status de todos os seus materiais</CardDescription>
              </CardHeader>
              <CardContent>
                {materiais.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nenhum material em processamento</p>
                ) : (
                  <div className="space-y-2">
                    {materiais.map((mat, idx) => {
                      const status = materialStatusConfig[mat.status] || materialStatusConfig.recebido;
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{mat.material_nome}</p>
                            <p className="text-xs text-slate-500">
                              Qtd: {mat.quantidade} | Recebido: {formatDate(mat.data_recebimento)}
                            </p>
                          </div>
                          <Badge className={status.class}>{status.label}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB SOLICITAÇÕES */}
          <TabsContent value="solicitacoes" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Minhas Solicitações</CardTitle>
                  <CardDescription>Histórico de coletas e entregas</CardDescription>
                </div>
                <Button onClick={() => setShowNovaSolicitacao(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Solicitação
                </Button>
              </CardHeader>
              <CardContent>
                {solicitacoes.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nenhuma solicitação realizada</p>
                ) : (
                  <div className="space-y-3">
                    {solicitacoes.map(sol => {
                      const status = statusConfig[sol.status] || statusConfig.pendente;
                      return (
                        <div key={sol.id} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-bold">{sol.numero}</span>
                              <Badge variant="outline">
                                {sol.tipo === 'coleta' ? 'Coleta' : 'Entrega'}
                              </Badge>
                              {sol.prioridade === 'urgente' && (
                                <Badge className="bg-red-100 text-red-800">Urgente</Badge>
                              )}
                            </div>
                            <Badge className={status.class}>{status.label}</Badge>
                          </div>
                          <div className="text-sm text-slate-600 space-y-1">
                            <p><Calendar className="w-4 h-4 inline mr-1" /> Solicitado: {sol.data_preferencial} {sol.horario_preferencial}</p>
                            {sol.data_confirmada && (
                              <p className="text-green-700 font-medium">
                                <CheckCircle className="w-4 h-4 inline mr-1" />
                                Agendado: {sol.data_confirmada} às {sol.horario_confirmado}
                              </p>
                            )}
                            {sol.itens?.length > 0 && (
                              <p><Package className="w-4 h-4 inline mr-1" /> {sol.itens.length} item(s)</p>
                            )}
                            {sol.observacoes && (
                              <p className="text-slate-500 italic">"{sol.observacoes}"</p>
                            )}
                          </div>
                          {sol.pagamento && (
                            <div className="mt-3 pt-3 border-t flex items-center justify-between gap-2 flex-wrap">
                              <PagamentoBadge pagamento={sol.pagamento} testId={`pagamento-status-${sol.id}`} />
                              {sol.status !== 'cancelada' && (
                                <Button size="sm" variant={sol.pagamento.status === 'pago' ? 'outline' : 'default'} className={sol.pagamento.status === 'pago' ? '' : 'bg-emerald-600 hover:bg-emerald-700'} onClick={() => setPixSolicitacao(sol)} data-testid={`btn-pagar-pix-${sol.id}`}>
                                  <QrCode className="w-4 h-4 mr-1" /> {sol.pagamento.status === 'pago' ? 'Ver pagamento' : 'Pagar com Pix'}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Dialog Nova Solicitação */}
      <Dialog open={showNovaSolicitacao} onOpenChange={setShowNovaSolicitacao}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {solicitacaoForm.tipo === 'coleta' ? 'Solicitar Coleta' : 'Solicitar Entrega'}
            </DialogTitle>
            <DialogDescription>
              {solicitacaoForm.tipo === 'coleta' 
                ? 'Solicite a coleta de materiais para esterilização'
                : 'Solicite a entrega dos materiais já esterilizados'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select
                  value={solicitacaoForm.tipo}
                  onValueChange={(v) => setSolicitacaoForm(f => ({...f, tipo: v}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coleta">Coleta</SelectItem>
                    <SelectItem value="entrega">Entrega</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prioridade</Label>
                <Select
                  value={solicitacaoForm.prioridade}
                  onValueChange={(v) => setSolicitacaoForm(f => ({...f, prioridade: v}))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Preferencial *</Label>
                <Input
                  type="date"
                  value={solicitacaoForm.data_preferencial}
                  onChange={(e) => setSolicitacaoForm(f => ({...f, data_preferencial: e.target.value}))}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Horário Preferencial</Label>
                <Input
                  type="time"
                  value={solicitacaoForm.horario_preferencial}
                  onChange={(e) => setSolicitacaoForm(f => ({...f, horario_preferencial: e.target.value}))}
                />
              </div>
            </div>
            
            {/* Itens */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Itens / Materiais</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" /> Adicionar
                </Button>
              </div>
              <div className="space-y-2">
                {solicitacaoForm.itens.map((item, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      placeholder="Descrição do item"
                      value={item.descricao}
                      onChange={(e) => updateItem(idx, 'descricao', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number" onFocus={(e) => e.target.select()}
                      placeholder="Qtd"
                      value={item.quantidade}
                      onChange={(e) => updateItem(idx, 'quantidade', parseInt(e.target.value) || 1)}
                      className="w-20"
                      min={1}
                    />
                    {solicitacaoForm.itens.length > 1 && (
                      <Button 
                        type="button" 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => removeItem(idx)}
                        className="text-red-500"
                      >
                        ×
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Observações</Label>
              <Textarea
                placeholder="Informações adicionais..."
                value={solicitacaoForm.observacoes}
                onChange={(e) => setSolicitacaoForm(f => ({...f, observacoes: e.target.value}))}
                rows={3}
              />
            </div>
          </div>
          
          {solicitacaoForm.tipo === 'coleta' && frete?.cobrar && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800" data-testid="aviso-frete-coleta">
              <QrCode className="w-4 h-4 mt-0.5 shrink-0" />
              <span>A coleta tem frete de <b>R$ {Number(frete.valor).toFixed(2).replace('.', ',')}</b>. Ao enviar, você recebe o QR Code Pix para pagamento.</span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovaSolicitacao(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCriarSolicitacao}>
              Enviar Solicitação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PixPagamentoDialog
        solicitacao={pixSolicitacao}
        open={!!pixSolicitacao}
        onClose={() => setPixSolicitacao(null)}
        onInformar={handleInformarPagamento}
      />
    </div>
  );
}
